import { Suspense, useCallback, useRef, useState } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { contactsApi } from '@/api/queries/contacts';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Avatar,
  Button,
  Drawer,
  Icon,
  Tag,
  colorFromKey,
  initialsOf,
  useToast,
} from '@/components/ui';

import { getFullName } from '../contactUtils';
import { GeneralNoteEditor } from '../GeneralNoteEditor';
import { ContactQuickDetails } from './ContactQuickDetails';

export function ContactQuickPanel({
  contactId,
  onClose,
}: {
  contactId: string;
  onClose: () => void;
}) {
  const dirtyRef = useRef(false);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const requestClose = () => {
    if (dirtyRef.current && !window.confirm('Discard unsaved changes?')) return;
    onClose();
  };

  return (
    <Drawer open onClose={requestClose} title="Contact" size="md" ariaLabel="Contact quick view">
      <Suspense fallback={<LoadingSpinner message="Loading contact..." />}>
        <QuickPanelContent
          key={contactId}
          contactId={contactId}
          onDirtyChange={handleDirtyChange}
        />
      </Suspense>
    </Drawer>
  );
}

function QuickPanelContent({
  contactId,
  onDirtyChange,
}: {
  contactId: string;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: contact } = contactsApi.useDetail(contactId);
  const addComment = contactsApi.useAddComment();
  const [commentText, setCommentText] = useState('');

  const fullName = getFullName(contact.profile);
  const comments = [...(contact.comments ?? [])].reverse();

  const submitComment = async () => {
    const trimmed = commentText.trim();
    if (!trimmed) return;
    await addComment.mutateAsync({ id: contactId, text: trimmed });
    setCommentText('');
    toast.show({ title: 'Comment added', severity: 'success' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar
          initials={initialsOf(contact.profile?.firstName, contact.profile?.lastName)}
          color={colorFromKey(contact.id ?? fullName)}
          size={32}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em' }}>{fullName}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
            {contact.contact?.primaryEmail ?? '—'}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="eye" size={12} />}
          onClick={() => navigate({ to: '/contacts/$id', params: { id: contactId } })}
        >
          Open contact
        </Button>
      </div>

      <ContactQuickDetails contact={contact} onDirtyChange={onDirtyChange} />

      <div>
        <div className="cqp-section-label">General note</div>
        <GeneralNoteEditor contactId={contactId} initialText={contact.generalNote} />
      </div>

      <div>
        <div className="cqp-section-label">Comments</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="ds-input"
            rows={2}
            placeholder="Leave a comment…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Icon name="send" size={12} />}
              disabled={!commentText.trim() || addComment.isPending}
              onClick={() => void submitComment()}
            >
              Add comment
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {comments.length === 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No comments yet.</span>
          )}
          {comments.map((c, i) => {
            const system = c.kind === 'system' || c.createdBy?.startsWith('system:');
            return (
              <div
                key={i}
                style={{
                  padding: 10,
                  borderRadius: 8,
                  background: system ? 'var(--surface-2)' : 'var(--surface-1)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  {system && <Tag tone="brand">Auto</Tag>}
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    {c.createdBy}
                    {c.createdAt && ` · ${new Date(c.createdAt).toLocaleString()}`}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-1)', whiteSpace: 'pre-wrap' }}>
                  {c.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
